import type { OrbCreditLedgerResponse } from 'api/types';
import { useGetCreditLedger } from 'entities/billing/api/useGetCreditLedger';

export type UsageSignals = {
  /** Percentage of granted credits consumed in the current cycle. 0-100+. */
  usagePct: number;
  /** True when more than one day in this cycle had a non-zero credit grant — i.e., a top-up was added. */
  topUpAdded: boolean;
  /** False while the underlying credit-ledger query is still loading. */
  isReady: boolean;
};

const startOfCurrentMonth = (now: Date) => new Date(now.getFullYear(), now.getMonth(), 1);
const startOfNextMonth = (now: Date) => new Date(now.getFullYear(), now.getMonth() + 1, 1);

const parseAmount = (value: string | undefined | null): number => {
  if (!value) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Pure derivation of the usage signals from a credit-ledger response. Split
 * out so unit tests can exercise the math without mocking the React-Query
 * layer.
 */
export const computeUsageSignals = (ledger: OrbCreditLedgerResponse): Omit<UsageSignals, 'isReady'> => {
  const startingBalance = parseAmount(ledger.total.starting_balance);
  const totalIncrement = parseAmount(ledger.total.increment?.amount);
  const totalDecrement = parseAmount(ledger.total.decrement?.amount);

  const granted = startingBalance + totalIncrement;
  const usagePct = granted > 0 ? (totalDecrement / granted) * 100 : 0;

  const incrementDays = ledger.daily.filter((day) => parseAmount(day.increment?.amount) > 0).length;
  const topUpAdded = incrementDays > 1;

  return { usagePct, topUpAdded };
};

/**
 * Estimate self-serve credit-cycle usage from the Orb credit ledger. The two
 * outputs feed `useUpgradeTrigger`:
 *   - `usagePct` for the static threshold check (≥ 80%)
 *   - `topUpAdded` for the "user bought a top-up this cycle" trigger
 *
 * Self-serve plans bill monthly with one upfront credit grant on day 1, so
 * any subsequent day with a non-zero increment is a top-up purchase.
 *
 * Pass ``enabled = false`` for users who can't trigger the self-serve promo
 * (individual / enterprise) so we don't spam the credit-ledger endpoint on
 * every dashboard render for those cohorts.
 */
export const useEstimatedUsagePct = ({ enabled = true }: { enabled?: boolean } = {}): UsageSignals => {
  const now = new Date();
  const { data, isPending } = useGetCreditLedger(
    {
      datetime_gte: startOfCurrentMonth(now).toISOString(),
      datetime_lt: startOfNextMonth(now).toISOString(),
    },
    { enabled },
  );

  if (!enabled || isPending || !data) {
    return { usagePct: 0, topUpAdded: false, isReady: false };
  }

  return { ...computeUsageSignals(data), isReady: true };
};
